import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlus, Search, Users, Trash2, Download, Upload } from 'lucide-react';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { BulkActionBar } from '@/components/ui/BulkActionBar';
import { FilterBar, type ActiveFilter, type FilterField } from '@/components/ui/FilterBar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Skeleton, SkeletonTable } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { SentimentIndicator } from '@/components/modules/SentimentIndicator';
import { EntityLink } from '@/components/ui/EntityLink';
import { ContactSheet } from '@/components/sheets/ContactSheet';
import { ImportContactsModal } from '@/components/modules/ImportContactsModal';
import { useContactsStore } from '@/stores/contactsStore';
import { useSelection } from '@/hooks/useSelection';
import { toast } from '@/stores/toastStore';
import { formatRelative } from '@/lib/format';
import { exportToCSV } from '@/lib/csv';
import type { Contact } from '@nexus/shared/types';

const typeVariant: Record<string, 'success' | 'info' | 'purple' | 'warning' | 'default'> = {
  customer: 'success',
  lead: 'info',
  partner: 'purple',
  vendor: 'warning',
  internal: 'default',
  other: 'default',
};

const CONTACT_FILTER_FIELDS: FilterField[] = [
  {
    key: 'type',
    label: 'Type',
    type: 'select',
    options: [
      { value: 'customer', label: 'Customer' },
      { value: 'lead', label: 'Lead' },
      { value: 'partner', label: 'Partner' },
      { value: 'vendor', label: 'Vendor' },
      { value: 'internal', label: 'Internal' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    key: 'sentiment',
    label: 'Sentiment',
    type: 'select',
    options: [
      { value: 'very_positive', label: 'Very Positive' },
      { value: 'positive', label: 'Positive' },
      { value: 'neutral', label: 'Neutral' },
      { value: 'negative', label: 'Negative' },
      { value: 'very_negative', label: 'Very Negative' },
    ],
  },
  { key: 'leadScore', label: 'Lead Score', type: 'number_range' },
  { key: 'name', label: 'Name', type: 'text' },
];

export function Contacts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, total, isLoading, error, fetch: fetchContacts, create, remove, update, bulkUpdate, bulkDelete, clearError } = useContactsStore();
  const { selected, toggle, toggleAll, clear: clearSelection, isAllSelected, count: selectionCount } = useSelection(items);

  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formType, setFormType] = useState('lead');
  const [formTags, setFormTags] = useState('');

  // Debounced search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doFetch = useCallback((q: string, activeFilters: ActiveFilter[]) => {
    const params: Record<string, string | number | undefined> = {
      q: q || undefined,
      sort: 'updatedAt',
      order: 'desc',
    };
    // Extract 'eq' type filter for the API query param (server-side filtering)
    const typeFilter = activeFilters.find((f) => f.key === 'type' && f.operator === 'eq' && f.value);
    if (typeFilter) params.type = typeFilter.value;
    fetchContacts(params);
  }, [fetchContacts]);

  // Initial fetch
  useEffect(() => {
    doFetch(search, filters);
  }, []);

  // Debounced refetch when search or filters change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doFetch(search, filters);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, filters, doFetch]);

  // Client-side filtering for fields not handled by the API
  const filteredItems = useMemo(() => {
    if (filters.length === 0) return items;
    return items.filter((contact) => {
      return filters.every((f) => {
        // Type with eq is handled server-side, skip re-filtering
        if (f.key === 'type' && f.operator === 'eq') return true;
        if (!f.value) return true; // empty value = no constraint

        const val = (contact as unknown as Record<string, unknown>)[f.key];
        const strVal = val != null ? String(val).toLowerCase() : '';
        const filterVal = f.value.toLowerCase();

        switch (f.operator) {
          case 'eq':
            return strVal === filterVal;
          case 'neq':
            return strVal !== filterVal;
          case 'contains':
            return strVal.includes(filterVal);
          case 'gt':
            return Number(val) > Number(f.value);
          case 'lt':
            return Number(val) < Number(f.value);
          default:
            return true;
        }
      });
    });
  }, [items, filters]);

  const resetForm = () => {
    setFormName('');
    setFormEmail('');
    setFormPhone('');
    setFormType('lead');
    setFormTags('');
    setCreateError(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;

    setCreateLoading(true);
    setCreateError(null);
    try {
      await create({
        type: formType,
        name: formName.trim(),
        email: formEmail.trim() || undefined,
        phone: formPhone.trim() || undefined,
        tags: formTags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      toast.success('Contact created', formName);
      setShowCreate(false);
      resetForm();
      doFetch(search, filters);
    } catch (err: unknown) {
      setCreateError((err as Error).message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreate(false);
    resetForm();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    try {
      await remove(confirmDelete);
      setConfirmDelete(null);
      doFetch(search, filters);
    } catch {
      /* handled by store */
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      await bulkDelete(Array.from(selected));
      clearSelection();
    } catch {
      // handled by store
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkChangeType = async (newType: string) => {
    if (selected.size === 0 || !newType) return;
    setBulkLoading(true);
    try {
      await bulkUpdate(Array.from(selected), { type: newType });
      clearSelection();
    } catch {
      // handled by store
    } finally {
      setBulkLoading(false);
    }
  };

  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => (
        <div>
          <EntityLink type="contact" id={row.id}>
            <span className="font-medium">{row.name}</span>
          </EntityLink>
          <p className="text-xs text-surface-500">{row.email || row.type}</p>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (row) => <span className="text-surface-300">{row.email || '-'}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) => <Badge variant={typeVariant[row.type] || 'default'}>{row.type}</Badge>,
    },
    {
      key: 'sentiment',
      header: 'Sentiment',
      render: (row) =>
        row.sentiment ? (
          <SentimentIndicator sentiment={row.sentiment} showLabel />
        ) : (
          <span className="text-surface-500 text-xs">-</span>
        ),
    },
    {
      key: 'leadScore',
      header: 'Lead Score',
      sortable: true,
      render: (row) => {
        const score = row.leadScore ?? 0;
        return (
          <div className="flex items-center gap-2">
            <div className="w-10 h-1.5 bg-surface-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-nexus-500 rounded-full"
                style={{ width: `${score}%` }}
              />
            </div>
            <span className="text-xs font-medium text-surface-300">{score}</span>
          </div>
        );
      },
    },
    {
      key: 'lastInteraction',
      header: 'Last Interaction',
      sortable: true,
      render: (row) => (
        <span className="text-surface-400 text-xs">
          {row.lastInteractionAt ? formatRelative(row.lastInteractionAt) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setConfirmDelete(row.id);
          }}
          className="p-1.5 rounded-lg text-surface-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          title="Delete contact"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ),
    },
  ];

  if (isLoading && items.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-6 w-32 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-9 w-32 rounded-lg" />
        </div>
        {/* Search bar skeleton */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 flex-1 max-w-sm rounded-lg" />
          <Skeleton className="h-10 w-40 rounded-lg" />
        </div>
        {/* Table skeleton — 7 columns matching the real table */}
        <SkeletonTable rows={8} cols={7} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-surface-100">Contacts</h2>
          <p className="text-sm text-surface-400 mt-0.5">{total} total contacts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            icon={<Download className="h-4 w-4" />}
            onClick={() =>
              exportToCSV(
                filteredItems,
                [
                  { key: 'name', header: 'Name' },
                  { key: 'email', header: 'Email' },
                  { key: 'phone', header: 'Phone' },
                  { key: 'type', header: 'Type' },
                  { key: 'sentiment', header: 'Sentiment' },
                  { key: 'leadScore', header: 'Lead Score' },
                ],
                `nexus-contacts-${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
            disabled={filteredItems.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="secondary"
            icon={<Upload className="h-4 w-4" />}
            onClick={() => setShowImport(true)}
          >
            Import CSV
          </Button>
          <Button icon={<UserPlus className="h-4 w-4" />} onClick={() => setShowCreate(true)}>
            Add Contact
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-between px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="text-xs text-red-400 hover:text-red-300">
            Dismiss
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-surface-500" />
        <input
          type="text"
          placeholder="Search by name, email, or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-200 placeholder-surface-500 focus:outline-none focus:ring-1 focus:ring-nexus-500"
        />
      </div>

      {/* Filters */}
      <FilterBar
        fields={CONTACT_FILTER_FIELDS}
        filters={filters}
        onChange={setFilters}
        storageKey="nexus:contacts-filters"
      />

      {/* Table */}
      {filteredItems.length === 0 && !isLoading ? (
        <EmptyState
          icon={<Users className="h-8 w-8" />}
          title="No contacts found"
          description={search || filters.length > 0 ? 'Try adjusting your search or filters.' : 'Create your first contact to get started.'}
          action={
            !search && filters.length === 0 ? (
              <Button icon={<UserPlus className="h-4 w-4" />} onClick={() => setShowCreate(true)}>
                Add Contact
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredItems}
            onRowClick={(row) => setSelectedId(row.id)}
            selectable
            selectedIds={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            isAllSelected={isAllSelected}
          />
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete Contact"
        description="This action cannot be undone. The contact and all associated data will be permanently removed."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteLoading}
      />

      {/* Create Contact Modal */}
      <Modal open={showCreate} onClose={handleCloseModal} title="Add Contact">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Name"
            placeholder="Full name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            required
          />
          <Input
            label="Email"
            type="email"
            placeholder="email@example.com"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
          />
          <Input
            label="Phone"
            type="tel"
            placeholder="+1 (555) 000-0000"
            value={formPhone}
            onChange={(e) => setFormPhone(e.target.value)}
          />
          <Select
            label="Type"
            options={[
              { value: 'lead', label: 'Lead' },
              { value: 'customer', label: 'Customer' },
              { value: 'partner', label: 'Partner' },
              { value: 'vendor', label: 'Vendor' },
              { value: 'internal', label: 'Internal' },
              { value: 'other', label: 'Other' },
            ]}
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
          />
          <Input
            label="Tags"
            placeholder="Comma-separated, e.g. enterprise, hot-lead"
            value={formTags}
            onChange={(e) => setFormTags(e.target.value)}
          />

          {createError && (
            <p className="text-sm text-red-400">{createError}</p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" loading={createLoading} disabled={!formName.trim()}>
              Create Contact
            </Button>
          </div>
        </form>
      </Modal>

      {/* Import Contacts Modal */}
      <ImportContactsModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onComplete={() => doFetch(search, filters)}
      />

      {/* Contact Detail Sheet */}
      <ContactSheet
        id={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />

      {/* Bulk Action Bar */}
      <BulkActionBar count={selectionCount} onClear={clearSelection}>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkDelete}
          loading={bulkLoading}
          icon={<Trash2 className="h-3.5 w-3.5" />}
        >
          Delete
        </Button>
        <select
          className="px-2 py-1 text-xs bg-surface-700 border border-surface-600 rounded-md text-surface-200 focus:outline-none focus:ring-1 focus:ring-nexus-500"
          defaultValue=""
          onChange={(e) => { handleBulkChangeType(e.target.value); e.target.value = ''; }}
        >
          <option value="" disabled>Change Type</option>
          <option value="customer">Customer</option>
          <option value="lead">Lead</option>
          <option value="partner">Partner</option>
          <option value="vendor">Vendor</option>
          <option value="internal">Internal</option>
          <option value="other">Other</option>
        </select>
      </BulkActionBar>
    </div>
  );
}
